import React from 'react';

import { ActiveLocation, ActiveLocationType } from '../common/active-location';
import {
  AirQualityMarkerProperties,
  AqiStandardProperties,
  NetworkProperties,
} from '../common/analytics/analytics-properties';
import { AnalyticsEventName } from '../common/analytics/types';
import { trackEvent, trackGroup } from '../common/analytics/utils';
import { calcDatasourcePollutantInfo, DatasourcePollutantInfo, findAqiLevel } from '../common/aqi';
import { decodeNetworkPreviewToken, PreviewToken } from '../common/data-preview';
import { SavedSettings } from '../common/saved-settings';
import { ISettings } from '../common/settings-interface';
import { SettingsKey } from '../common/settings-key';
import { Viewport } from '../common/viewport';
import { isEmbedded } from '../common/widgets-utils';
import {
  AirQualityMarkerDto,
  AqiLevelDto,
  AqiStandardId,
  AqiStandardV2Dto,
  DatasourceId,
  MapSettingsDto,
  MetricIndex,
  MetricKey,
  MonitoringDeviceId,
  MonitoringDeviceInfoDto,
  NetworkId,
  NetworkV2Dto,
  OpenSiteDto,
  OpenSiteId,
  Point,
  SensedConfigDto,
  SourceType,
} from '../types';
import { computeDistancesFromCoords } from '../common/location-utils';

interface SettingsContextType {
  setDatasourceId: (datasourceId: DatasourceId | null) => void;

  marker: AirQualityMarkerDto | null;
  setAvailableMarkers: (markers: AirQualityMarkerDto[] | null) => void;
  availableMarkers: AirQualityMarkerDto[] | null;

  // For data simulation and testing
  setMarkerOverride: (marker: AirQualityMarkerDto) => void;

  mapSettings: MapSettingsDto | null;
  setMapSettings: (mapSettings: MapSettingsDto | null) => void;

  monitoringDeviceInfos: Record<MonitoringDeviceId, MonitoringDeviceInfoDto> | null;
  setMonitoringDeviceInfos: (monitoringDeviceInfos: Record<MonitoringDeviceId, MonitoringDeviceInfoDto> | null) => void;

  network: NetworkV2Dto | null;
  setNetwork(network: NetworkV2Dto): void;
  networkId: NetworkId | null;

  setAvailableOpenSites: (openSites: OpenSiteDto[] | null) => void;
  availableOpenSites: OpenSiteDto[] | null;
  selectedOpenSite: OpenSiteDto | null;
  openSiteRadius: number | null;

  aqiStandard: AqiStandardV2Dto | null;
  availableAqiStandards: AqiStandardV2Dto[] | null;
  setAvailableAqiStandards: (aqiStandards: AqiStandardV2Dto[]) => void;
  setAqiStandardId: (aqiStandardId: AqiStandardId) => void;
  viewport: Viewport | null;
  setViewport: (viewport: React.SetStateAction<Viewport | null>) => void;

  isSettingsLoaded: boolean;
  loadSettings: (settingsStore: ISettings[]) => void;

  isLocationPermissionGranted: boolean | null;
  setLocationPermissionGranted: (granted: boolean) => void;

  // calculated values:
  sensedConfigs: SensedConfigDto[] | null;
  datasourcePollutantInfos: DatasourcePollutantInfo[] | null;
  aqiLevel: AqiLevelDto | null;
  aqiMetricKeys: MetricKey[] | null;
  savedSettings: SavedSettings | null;

  activeLocation: ActiveLocation | null;
  setMyLocationActive: (point: Point) => void;
  setDatasourceActive: (datasourceId: DatasourceId) => void;
  setOpenSiteActive: (openSiteId: OpenSiteId) => void;
  setReverseGeocodedLocationActive: (point: Point) => void;
  clearActiveLocation: () => void;
}

const SettingsContext = React.createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: React.ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [datasourceId, _setDatasourceId] = React.useState<DatasourceId | null>(null);
  const [openSiteId, _setOpenSiteId] = React.useState<OpenSiteId | null>(null);
  const [availableMarkers, setAvailableMarkers] = React.useState<AirQualityMarkerDto[] | null>(null);
  const [network, _setNetwork] = React.useState<NetworkV2Dto | null>(null);
  const [availableAqiStandards, setAvailableAqiStandards] = React.useState<AqiStandardV2Dto[] | null>(null);
  const [networkId, setNetworkId] = React.useState<NetworkId | null>(null);
  const [aqiStandardId, _setAqiStandardId] = React.useState<AqiStandardId | null>(null);
  const [mapSettings, setMapSettings] = React.useState<MapSettingsDto | null>(null);
  const [monitoringDeviceInfos, setMonitoringDeviceInfos] = React.useState<Record<
    MonitoringDeviceId,
    MonitoringDeviceInfoDto
  > | null>(null);
  const [viewport, setViewport] = React.useState<Viewport | null>(null);
  const [isLocationPermissionGranted, setLocationPermissionGranted] = React.useState<boolean | null>(null);
  const [isSettingsLoaded, setIsSettingsLoaded] = React.useState<boolean>(false);
  const [networkOverride, setNetworkOverride] = React.useState<Partial<NetworkV2Dto> | null>(null);
  const [aqiStandardOverride, setAqiStandardOverride] = React.useState<Partial<AqiStandardV2Dto> | null>(null);
  const [availableOpenSites, setAvailableOpenSites] = React.useState<OpenSiteDto[] | null>(null);
  const [activeLocation, setActiveLocation] = React.useState<ActiveLocation | null>(null);

  const savedSettings = React.useMemo(() => {
    if (!networkId) {
      return null;
    }
    const settings = new SavedSettings(isEmbedded); // disabled if in widget mode
    if (settings.networkId !== networkId) {
      settings.clear();
      settings.networkId = networkId;
    }
    return settings;
  }, [networkId]);

  // Set the active location when the open site id changes
  React.useEffect(() => {
    if (!openSiteId || !availableOpenSites) {
      return;
    }
    const point = availableOpenSites?.find((s) => s.openSiteId === openSiteId)?.location;
    if (!point) {
      return;
    }
    setActiveLocation({ type: ActiveLocationType.OpenSite, openSiteId, point });
  }, [openSiteId, availableOpenSites]);

  const setReverseGeocodedLocationActive = React.useCallback((point: Point) => {
    setActiveLocation({ type: ActiveLocationType.ReverseGeocodedLocation, point });
  }, []);

  const setDatasourceId = React.useCallback(
    (datasourceId: DatasourceId | null) => {
      _setDatasourceId(datasourceId);
      if (savedSettings) {
        savedSettings.datasourceId = datasourceId;
      }
    },
    [savedSettings]
  );

  const clearActiveLocation = React.useCallback(() => {
    setActiveLocation(null);
    _setOpenSiteId(null);
    if (savedSettings) {
      savedSettings.openSiteId = null;
      savedSettings.searchedLocation = null;
    }
  }, [savedSettings]);

  const setMyLocationActive = React.useCallback((point: Point) => {
    setActiveLocation({ type: ActiveLocationType.MyLocation, point });
  }, []);

  const setDatasourceActive = React.useCallback(
    (datasourceId: DatasourceId) => {
      const point = availableMarkers?.find((m) => m.datasourceId === datasourceId)?.location;
      if (!point) {
        return;
      }
      setDatasourceId(datasourceId);
      setActiveLocation({ type: ActiveLocationType.Datasource, datasourceId, point });
    },
    [availableMarkers, setDatasourceId]
  );

  const setOpenSiteActive = React.useCallback(
    (openSiteId: OpenSiteId) => {
      _setOpenSiteId(openSiteId);
      if (savedSettings) {
        savedSettings.openSiteId = openSiteId;
        savedSettings.searchedLocation = null;
      }
    },
    [savedSettings]
  );

  const setAqiStandardId = React.useCallback(
    (aqiStandardId: AqiStandardId | null) => {
      _setAqiStandardId(aqiStandardId);
      if (savedSettings) {
        savedSettings.aqiStdId = aqiStandardId;
      }
    },
    [savedSettings]
  );

  const loadSettings = React.useCallback(
    (settingsArray: ISettings[]) => {
      if (isSettingsLoaded) {
        return;
      }

      // Remove empty settings
      settingsArray = settingsArray.filter((s) => !s.isEmpty());
      if (settingsArray.length === 0) {
        return;
      }

      function findReferenceSetting(key: keyof ISettings) {
        return settingsArray.find((s) => s[key] !== null) ?? null;
      }

      function findReferenceSettingsValue<T>(key: keyof ISettings) {
        return (findReferenceSetting(key)?.[key] as T | undefined) ?? null;
      }

      function isConflictingSettingsKey(key: keyof ISettings) {
        const cleanSettingsArray = settingsArray.filter((s) => s[key] !== null);
        if (cleanSettingsArray.length === 0) {
          return false;
        }
        return cleanSettingsArray.some((s) => s[key] !== cleanSettingsArray[0][key]);
      }

      const referenceSetting = findReferenceSetting(SettingsKey.networkId);
      const networkId = findReferenceSettingsValue<NetworkId>(SettingsKey.networkId);

      // To avoid picking a setting value that is not valid for the network,
      // we'll stick to the reference setting.
      const isNetworkIdConflicting = isConflictingSettingsKey(SettingsKey.networkId);
      let datasourceId = null;
      let aqiStdId = null;
      let searchedLatitude = null;
      let searchedLongitude = null;
      let preview = null;
      let openSiteId = null;
      if (isNetworkIdConflicting) {
        datasourceId = referenceSetting?.datasourceId ?? null;
        aqiStdId = referenceSetting?.aqiStdId ?? null;
        searchedLatitude = referenceSetting?.searchedLatitude ?? null;
        searchedLongitude = referenceSetting?.searchedLongitude ?? null;
        preview = referenceSetting?.preview ?? null;
        openSiteId = referenceSetting?.openSiteId ?? null;
      } else {
        datasourceId = findReferenceSettingsValue<DatasourceId>(SettingsKey.datasourceId);
        aqiStdId = findReferenceSettingsValue<AqiStandardId>(SettingsKey.aqiStdId);
        searchedLatitude = findReferenceSettingsValue<number>(SettingsKey.searchedLatitude);
        searchedLongitude = findReferenceSettingsValue<number>(SettingsKey.searchedLongitude);
        preview = findReferenceSettingsValue<PreviewToken>(SettingsKey.preview);
        openSiteId = findReferenceSettingsValue<OpenSiteId>(SettingsKey.openSiteId);
      }

      setNetworkId(networkId);
      _setDatasourceId(datasourceId);
      _setAqiStandardId(aqiStdId);

      if (openSiteId) {
        _setOpenSiteId(openSiteId);
      } else if (searchedLatitude && searchedLongitude) {
        setActiveLocation({
          point: { type: 'Point', coordinates: [searchedLongitude, searchedLatitude] },
          type: ActiveLocationType.ReverseGeocodedLocation,
        });
      }

      const latitude = findReferenceSettingsValue<number>(SettingsKey.latitude);
      const longitude = findReferenceSettingsValue<number>(SettingsKey.longitude);
      const zoom = findReferenceSettingsValue<number>(SettingsKey.zoom);
      const hasViewport = latitude !== null && longitude !== null && zoom !== null;
      if (hasViewport) {
        setViewport({ longitude, latitude, zoom });
      } else {
        // Should never happen
        setViewport(null);
      }

      if (preview) {
        const { network: networkOverride, aqiStandard: aqiStandardOverride } = decodeNetworkPreviewToken(preview);
        setNetworkOverride(networkOverride ?? null);
        setAqiStandardOverride(aqiStandardOverride ?? null);
      } else {
        setNetworkOverride(null);
        setAqiStandardOverride(null);
      }
      setIsSettingsLoaded(true);
    },
    [isSettingsLoaded]
  );

  const setNetwork = React.useCallback(
    (network: NetworkV2Dto | null) => {
      if (network && networkOverride) {
        network = { ...network, ...networkOverride };
      }
      _setNetwork(network);
      const networkId = network?.networkId ?? null;
      setNetworkId(networkId);

      // Track the network as long as it's not an override (preview)
      if (network && !networkOverride) {
        const networkProperties: NetworkProperties = {
          name: network.name,
          visible: network.visible,
          sensedKeys: network.selectedSensedItems.map((ssi) => ssi.sensedKey),
          isNotificationEnabled: network.isNotificationEnabled,
        };
        trackGroup('network', network.networkId, networkProperties);
      }
    },
    [networkOverride]
  );

  const setMarkerOverride = React.useCallback(
    (override: AirQualityMarkerDto) => {
      const index = availableMarkers?.findIndex((m) => m.datasourceId === override.datasourceId);
      if (index === undefined || index === -1) {
        return;
      }
      setAvailableMarkers([
        ...(availableMarkers ?? []).slice(0, index),
        override,
        ...(availableMarkers ?? []).slice(index + 1),
      ]);
    },
    [availableMarkers]
  );

  const marker = React.useMemo(() => {
    return availableMarkers?.find((m) => m.datasourceId === datasourceId) ?? null;
  }, [datasourceId, availableMarkers]);

  const selectedOpenSite = React.useMemo(() => {
    return activeLocation?.type === ActiveLocationType.OpenSite
      ? (availableOpenSites?.find((s) => s.openSiteId === activeLocation.openSiteId) ?? null)
      : null;
  }, [activeLocation, availableOpenSites]);

  const nodeMarkers = React.useMemo(() => {
    return availableMarkers?.filter((m) => m.sourceType === SourceType.CLARITY_NODE) ?? null;
  }, [availableMarkers]);

  const openSiteToNodeMarkerDistances = React.useMemo(() => {
    if (!selectedOpenSite || !nodeMarkers) {
      return null;
    }
    return computeDistancesFromCoords(
      selectedOpenSite.location.coordinates,
      nodeMarkers.map((m) => m.location.coordinates),
      'meters'
    );
  }, [selectedOpenSite, nodeMarkers]);

  const openSiteRadius = React.useMemo(() => {
    if (!selectedOpenSite || !openSiteToNodeMarkerDistances) {
      return null;
    }
    if (selectedOpenSite.radiusOverride !== undefined && selectedOpenSite.radiusOverride !== null) {
      return selectedOpenSite.radiusOverride;
    }
    const nearestDist = Math.min(...openSiteToNodeMarkerDistances);
    const slope = network?.openSiteRadiusSlope ?? 1.25;
    const offset = network?.openSiteRadiusOffset ?? 0.5;
    return slope * nearestDist + offset;
  }, [selectedOpenSite, openSiteToNodeMarkerDistances]);

  const aqiStandard = React.useMemo(() => {
    const aqiStandard = availableAqiStandards?.find((s) => s.aqiStandardId === aqiStandardId) ?? null;

    if (aqiStandard && aqiStandardOverride) {
      return { ...aqiStandard, ...aqiStandardOverride };
    }
    return aqiStandard;
  }, [aqiStandardId, availableAqiStandards, aqiStandardOverride]);

  const sensedConfigs = React.useMemo(() => {
    if (!network || !aqiStandard) {
      return null;
    }
    return aqiStandard.sensedConfigs.filter(({ sensedKey }) =>
      network.selectedSensedItems.some((ssi) => ssi.sensedKey === sensedKey)
    );
  }, [network, aqiStandard]);

  const datasourcePollutantInfos = React.useMemo(() => {
    if (!marker || !sensedConfigs || !aqiStandard) {
      return null;
    }
    return calcDatasourcePollutantInfo(marker.metrics, sensedConfigs, aqiStandard);
  }, [marker, sensedConfigs, aqiStandard]);

  const aqiLevel = React.useMemo(() => {
    if (!aqiStandard || (datasourcePollutantInfos?.length ?? 0) === 0) {
      return null;
    }
    return findAqiLevel(aqiStandard, datasourcePollutantInfos?.[0]?.aqi);
  }, [aqiStandard, datasourcePollutantInfos]);

  const pollutantInfos = React.useMemo(() => {
    if (!sensedConfigs) {
      return null;
    }
    return sensedConfigs
      .filter((config) => config.isPollutant)
      .map(({ sensedKey, displayName, metricInfos }) => ({
        sensedKey,
        displayName,
        aqiInfo: metricInfos.find((info) => info.indexType == MetricIndex.Aqi),
        concInfo: metricInfos.find((info) => info.indexType == MetricIndex.Unindexed)!,
      }));
  }, [sensedConfigs]);

  const aqiMetricKeys = React.useMemo(() => {
    if (!pollutantInfos) {
      return null;
    }
    return pollutantInfos
      .filter((p) => p.aqiInfo)
      .map((p) => p.aqiInfo!.key)
      .flat();
  }, [pollutantInfos]);

  const truncatedViewport: Viewport | null = React.useMemo(() => {
    if (!viewport) {
      return null;
    }
    // About 100m precision
    const latitude = Math.round(viewport.latitude * 1000) / 1000;
    const longitude = Math.round(viewport.longitude * 1000) / 1000;
    const zoom = Math.round(viewport.zoom * 100) / 100;
    return { latitude, longitude, zoom };
  }, [viewport]);

  // Set the selected open site location
  React.useEffect(() => {
    if (selectedOpenSite) {
      setActiveLocation({
        type: ActiveLocationType.OpenSite,
        openSiteId: selectedOpenSite.openSiteId,
        point: selectedOpenSite.location,
      });
    } else {
      setActiveLocation(null);
    }
  }, [selectedOpenSite]);

  // Set the default aqi standard
  // Note: we can't use DefaultSettings for this because of a dependency cycle.
  React.useEffect(() => {
    if (!aqiStandardId && availableAqiStandards?.length) {
      const defaultStandard = availableAqiStandards[0];
      _setAqiStandardId(defaultStandard.aqiStandardId);
    }
  }, [aqiStandardId, availableAqiStandards]);

  // Save viewport settings
  React.useEffect(() => {
    if (!savedSettings || !viewport) {
      return;
    }

    // Throttle the updates to once every 500ms using a timeout
    const timeoutId = setTimeout(() => {
      // Save the settings
      savedSettings.latitude = viewport.latitude;
      savedSettings.longitude = viewport.longitude;
      savedSettings.zoom = viewport.zoom;
    }, 500);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
    };
  }, [viewport, savedSettings]);

  // -----------------------------------------------------------------------
  // Analytics
  // -----------------------------------------------------------------------
  const myLocation = React.useMemo(() => {
    return activeLocation?.type === ActiveLocationType.MyLocation ? activeLocation.point : null;
  }, [activeLocation]);

  const searchedLocation = React.useMemo(() => {
    return activeLocation?.type === ActiveLocationType.ReverseGeocodedLocation ? activeLocation.point : null;
  }, [activeLocation]);

  const openSiteLocation = React.useMemo(() => {
    return activeLocation?.type === ActiveLocationType.OpenSite ? activeLocation.point : null;
  }, [activeLocation]);

  // Track the aqi standard
  React.useEffect(() => {
    if (aqiStandard) {
      const sensedConfigs = aqiStandard.sensedConfigs.map(({ sensedKey, displayName }) => ({
        sensedKey,
        displayName,
      }));
      const eventProperties: AqiStandardProperties = {
        aqiStandardId: aqiStandard.aqiStandardId,
        name: aqiStandard.name,
        aqiClassType: aqiStandard.aqiClassType,
        sensedConfigs,
      };
      trackEvent(AnalyticsEventName.AqiStandardChanged, eventProperties);
    }
  }, [aqiStandard]);

  // Track marker
  React.useEffect(() => {
    let eventProperties: AirQualityMarkerProperties | undefined = undefined;
    if (marker) {
      eventProperties = {
        datasourceId: marker.datasourceId,
        sourceType: marker.sourceType,
        datasourceName: marker.datasourceName,
        sampledAt: marker.sampledAt,
        location: marker.location,
      };
    }
    trackEvent(AnalyticsEventName.MarkerChanged, eventProperties);
  }, [marker]);

  // Track my location
  React.useEffect(() => {
    trackEvent(AnalyticsEventName.MyLocationChanged, {
      location: myLocation,
    });
  }, [myLocation]);

  // Track searched location
  React.useEffect(() => {
    trackEvent(AnalyticsEventName.SelectedSearchedLocationChanged, {
      location: searchedLocation,
    });
  }, [searchedLocation]);

  // Track open site location
  React.useEffect(() => {
    trackEvent(AnalyticsEventName.SelectedOpenSiteLocationChanged, {
      location: openSiteLocation,
    });
  }, [openSiteLocation]);

  // Track viewport changes
  React.useEffect(
    () => {
      if (!truncatedViewport) {
        return;
      }

      // Throttle the updates to once every 5 seconds using a timeout
      const timeoutId = setTimeout(() => {
        trackEvent(AnalyticsEventName.ViewportChanged, {
          viewport: truncatedViewport,
        });
      }, 5_000);

      // Cleanup
      return () => {
        clearTimeout(timeoutId);
      };
    },
    // Monitor relevant changes to the values instead of the whole object
    [truncatedViewport?.latitude, truncatedViewport?.longitude, truncatedViewport?.zoom]
  );

  // Track location permission changes
  React.useEffect(() => {
    if (isLocationPermissionGranted === null) {
      return;
    }
    trackEvent(AnalyticsEventName.LocationPermissionChanged, {
      locationPermissionGranted: isLocationPermissionGranted,
      locationPermissionDenied: !isLocationPermissionGranted,
    });
  }, [isLocationPermissionGranted]);

  return (
    <SettingsContext.Provider
      value={{
        marker,
        network,
        setNetwork,
        aqiStandard,
        availableAqiStandards,
        setAvailableAqiStandards,
        setAqiStandardId,
        sensedConfigs,
        datasourcePollutantInfos,
        aqiLevel,
        networkId,
        aqiMetricKeys,
        mapSettings,
        setMapSettings,
        monitoringDeviceInfos,
        setMonitoringDeviceInfos,
        savedSettings,
        viewport,
        setViewport,
        isSettingsLoaded,
        loadSettings,
        setDatasourceId,
        setAvailableMarkers,
        availableMarkers,
        isLocationPermissionGranted,
        setLocationPermissionGranted,
        selectedOpenSite,
        openSiteRadius,
        setAvailableOpenSites,
        availableOpenSites,
        setMarkerOverride,
        activeLocation,
        setMyLocationActive,
        setDatasourceActive,
        setOpenSiteActive,
        setReverseGeocodedLocationActive,
        clearActiveLocation,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = React.useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Accept HMR updates to prevent context loss during Fast Refresh
if (import.meta.hot) {
  import.meta.hot.accept();
}
